import { CountrySelect } from '@/views/screens/common';
import { Col, Row } from 'antd';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { calculateCube, generateStopNumbers } from '../../../../../../../utilz';
import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormCheckBox from '../../../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormSelectBox from '../../../../../../components/Inputs/FormSelectBox';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import HalPortNameSelector from '../../../../../../components/Selectors/HalPortNameSelector';

import QuotePortTypeSelector from '@/views/components/Selectors/QuotePortTypeSelector';
import QuoteStopTypeSelector from '@/views/components/Selectors/QuoteStopTypeSelector';
import {
  countryCodeRules,
  portCityRules,
  portNameRules,
  portTypeRules,
  stopSequenceRules,
  stopTypeIdRules,
} from './constants';

const HallTransportationDetailsCard = ({
  index,
  name,
  stopDetails = {},
  handleDataChange,
  isLast = false,
  form,
  totalStops = 0,
}) => {
  const { stopTypeID } = stopDetails;
  const editMode = Boolean(stopDetails.editMode);

  const cube = useMemo(
    () =>
      calculateCube(
        stopDetails?.length,
        stopDetails?.width,
        stopDetails?.height,
        stopDetails?.billQty,
        stopDetails?.dimUom,
      ),
    [stopDetails?.length, stopDetails?.width, stopDetails?.height, stopDetails?.billQty, stopDetails?.dimUom],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
  }, [cube]);

  useEffect(() => {
    handleDataChange({ isValidLocation: true }, index);
  }, [stopDetails?.postalCode]);

  const handlePortDetails = (data) => {
    handleDataChange(
      {
        portName: data?.portName,
        portRecKey: data?.portRecKey,
        portCode: data?.portCode,
        portType: data?.portType,
      },
      index,
    );
    if (data?.state) {
      handleDataChange({ isValidLocation: true }, index);
    } else {
      handleDataChange({ isValidLocation: false }, index);
    }
  };

  const handlePortTypeChange = () => {
    handleDataChange(
      {
        portRecKey: undefined,
        portCode: undefined,
        portName: undefined,
      },
      index,
    );
  };

  return (
    <FilterCard
      shadow={false}
      showBorder
      className={isLast === false ? 'mb-15' : ''}
      labelForTestId="transportation-details-card"
      customHeader={
        <div className="w-100">
          <Row justify="space-between" align="middle">
            <Col flex={1}>
              <Row gutter={15} align="middle">
                <Col>
                  <FormCheckBox
                    name={[name, 'selected']}
                    checkBoxClass="normal-checkbox"
                    className="m-0"
                    label={false}
                    dataTestId="transportation-stop-checkbox"
                  />
                </Col>
                <Col sm={6} data-testid="transportation-stop-type">
                  <QuoteStopTypeSelector
                    dataTestId="transportation-stop-type-selector"
                    autoFocus={editMode}
                    name={[name, 'stopTypeId']}
                    checkBoxClass="normal-checkbox"
                    className="m-0"
                    label={
                      <FormLabel className="f-12 fw-400">
                        {t('translation:getQuote.transportationDetails.stopType.label')}
                      </FormLabel>
                    }
                    singleLineMode
                    colon
                    editMode={index !== 0 && editMode}
                    margin="m-0"
                    rules={stopTypeIdRules(t, form)}
                    hideDelivery={totalStops === 1}
                  />
                </Col>
                <Col sm={6} data-testid="transportation-stop-no">
                  <FormSelectBox
                    data-testid="transportation-stop-no-selector"
                    label={t('translation:getQuote.transportationDetails.stopNo.label')}
                    editMode={editMode && stopTypeID > 0}
                    singleLineMode
                    colon
                    name={[name, 'stopSequence']}
                    placeholder={t('translation:getQuote.transportationDetails.stopNo.placeholder')}
                    margin="m-0"
                    options={generateStopNumbers(totalStops, stopDetails?.stopTypeID)}
                    rules={stopSequenceRules(t)}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row justify="space-between" align="top">
          <Col sm={4}>
            <CountrySelect
              dataTestId="transportation-country-selector"
              label={t('translation:default.fields.country.label')}
              name={[name, 'countryCode']}
              editMode={editMode}
              margin="m-0"
              rules={countryCodeRules(t)}
            />
          </Col>
          <Col sm={4}>
            <FormInput
              name={[name, 'portCity']}
              label={t('translation:default.fields.city.label')}
              margin="m-0"
              editMode={editMode}
              rules={portCityRules(t)}
              dataTestId="stop-port-city-field"
            />
          </Col>
          <Col sm={2}>
            <QuotePortTypeSelector
              name={[name, 'portType']}
              label={t('translation:default.fields.portType.label')}
              margin="m-0"
              editMode={editMode}
              onChange={handlePortTypeChange}
              rules={portTypeRules(form)}
              dependencies={[[name, 'portName']]}
              dataTestId="stop-port-type-selector"
            />
          </Col>
          <Col sm={5}>
            <HalPortNameSelector
              margin="m-0"
              name={[name, 'portName']}
              editMode={editMode}
              portRecKey={stopDetails?.portRecKey}
              portType={stopDetails?.portType}
              portCode=""
              stateCode={stopDetails?.state}
              countryCode={stopDetails?.countryCode}
              zip={stopDetails?.postalCode}
              handlePortDetails={handlePortDetails}
              dependencies={[[name, 'portType']]}
              rules={portNameRules(form)}
              readOnly
              dataTestId="stop-port-name-selector"
            />
          </Col>
          <Col sm={7}>
            <FormInput
              name={[name, 'address']}
              label={t('translation:default.fields.address.label')}
              margin="m-0"
              editMode={editMode}
              dataTestId="stop-port-address"
            />
          </Col>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'isValidLocation']} />
      <HiddenFormInput name={[name, 'portRecKey']} />
      <HiddenFormInput name={[name, 'portCode']} />
    </FilterCard>
  );
};

export default HallTransportationDetailsCard;
